"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { validateEmail } from "@/lib/security/validation"
import { Mail } from "lucide-react"

interface EmailGateProps {
  onSubmit: (email: string) => void
}

export function EmailGate({ onSubmit }: EmailGateProps) {
  const [email, setEmail] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      validateEmail(email)
      onSubmit(email)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Please enter a valid email address")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2 mb-2">
          <Mail className="h-5 w-5 text-primary" />
          <CardTitle>Continue Your Review</CardTitle>
        </div>
        <CardDescription>
          We'll save your progress so you can return anytime. Your email is only used to save your review.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError(null)
              }}
              required
              disabled={isSubmitting}
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Continuing..." : "Continue"}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            We respect your privacy. Your email is only used to save your review.
          </p>
        </form>
      </CardContent>
    </Card>
  )
}

